import { action, makeObservable, observable } from 'mobx';
import type { CollectionModelFieldHeight } from '../fields/height.field';
import type { CollectionModelFieldVerticalSeparation } from '../fields/vertical.separation.field';
import {
	type ApprovableInsightDrawable,
	type CollectionModelDrawable,
	type DrawableWithLabel,
	MoveableDrawable,
} from './drawable';
import { isBaseMarker, isBaseOffsetMarker, isMarker, isVerticalSeparationDrawable } from './drawable.guards';
import { DEFAULT_SCALE, type SVGRenderOptions } from '../../../draw';
import { LABEL_SIZE } from '../../../draw/drawable/label.draw.svg';
import { DEFAULT_CAPTURE_SIZE } from '../../capture';
import type { CollectionModelDrawableState } from './drawable.state';

/**
 * Label has rows of text.
 * Label is attached to a parent Marker or Line, identified by "parentId".
 */
export class CollectionModelDrawableLabel extends MoveableDrawable implements ApprovableInsightDrawable {
	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		drawableId: string,
		x: number,
		y: number,
		parentId: string,
		text: string[] = [],
		links: string[] = []
	) {
		super(collectionModelDrawableState, drawableId);
		makeObservable(this);

		this.x = x;
		this.y = y;
		this._parentId = parentId;
		this._text = text;
		this._links = links;
	}

	static isLabel(t: CollectionModelDrawable): t is CollectionModelDrawableLabel {
		return t instanceof CollectionModelDrawableLabel;
	}

	@observable override x: number = 0;
	@observable override y: number = 0;

	/**
	 * Move the label to this location
	 *
	 * @param x coordinates in pixels
	 * @param y coordinates in pixels
	 */
	@action override move(x: number, y: number) {
		const bounds = this.getPointInBounds(x, y);
		this.x = bounds.x;
		this.y = bounds.y;
	}

	protected _parentId: string;

	get parentId() {
		return this._parentId;
	}

	get parent(): CollectionModelDrawable & DrawableWithLabel {
		return this.drawableState.getDrawable(this._parentId);
	}

	/**
	 * @deprecated Use {@link getDisplayText} instead.
	 */
	@observable protected _text: string[] | null = null;

	/**
	 * @deprecated Use {@link getDisplayText} instead.
	 */
	get text(): string[] {
		if (this.parent != null) {
			if (isBaseMarker(this.parent)) {
				if (this.drawableState.baseOffset == null) {
					return ['Base'];
				}
				return ['Base offset'];
			}

			if (isBaseOffsetMarker(this.parent)) {
				return ['Base'];
			}
		}
		return this._text;
	}

	/**
	 * @deprecated Use {@link getDisplayText} instead.
	 */
	set text(value: string[]) {
		this._text = value;
	}

	getDisplayText(options: SVGRenderOptions): string[] {
		if (isBaseMarker(this.parent)) {
			if (this.drawableState.baseOffset == null) {
				return ['Base'];
			}
			return ['Base offset'];
		}
		if (isBaseOffsetMarker(this.parent)) {
			return ['Base'];
		}
		if (isVerticalSeparationDrawable(this.parent)) {
			return this.parent.getLabelDisplayText(options);
		}
		return [];
	}

	/**
	 * Field IDs
	 */
	protected _links: string[];

	/** Only height & vertical separation fields can be linked to */
	get links() {
		if (!Array.isArray(this._links)) {
			return [];
		}
		const output: (CollectionModelFieldHeight | CollectionModelFieldVerticalSeparation)[] = [];
		const collection = this.drawableState.collection;
		for (const link of this._links) {
			const collectionField = collection.findField<CollectionModelFieldHeight | CollectionModelFieldVerticalSeparation>(
				link
			);
			if (collectionField == null) {
				continue;
			}
			output.push(collectionField);
		}
		return output;
	}

	@action linkHeight(field: CollectionModelFieldHeight) {
		if (!this.parent || !isMarker(this.parent)) {
			throw new Error(`Attempted to link unlinkable label "${this.id}" (parent must be Marker)`);
		}

		const links = this._links.slice();
		links.push(field.id);
		this._links = links;

		if (field.stateValue?.drawable !== this.parentId) {
			field.Mutator.drawable(this.parent);
		}
	}

	@action linkVerticalSeparation(field: CollectionModelFieldVerticalSeparation) {
		if (!this.parent || !isMarker(this.parent)) {
			throw new Error(`Attempted to link unlinkable label "${this.id}" (parent must be Marker)`);
		}

		const links = this._links.slice();
		links.push(field.id);
		this._links = links;

		if (field.stateValue?.drawableTop !== this.parentId && field.stateValue?.drawableBottom !== this.parentId) {
			field.Mutator.drawable(this.parent);
		}
	}

	/**
	 * Remove entries from `_links` whose field ID no longer exists in the collection.
	 * Unlike `unlink()`, this does NOT auto-remove the label when links become empty.
	 */
	@action pruneInvalidLinks() {
		const collection = this.drawableState.collection;
		this._links = this._links.filter((link) => collection.findField(link) != null);
	}

	@action unlink(fieldId: string) {
		const fieldIdIndex = this._links.indexOf(fieldId);
		if (fieldIdIndex === -1) {
			return;
		}
		const links = this._links.slice();
		links.splice(fieldIdIndex, 1);
		this._links = links;

		// If this was the last link to be removed, automatically remove the label as well.
		if (this._links.length === 0) {
			this.remove();
		}
	}

	snapToLeftSide(): void {
		this.move(0, this.y);
	}

	snapToRightSide(): void {
		this.move(
			(this.drawableState.capture?.resolution.width ?? DEFAULT_CAPTURE_SIZE.width) - LABEL_SIZE.width * DEFAULT_SCALE,
			this.y
		);
	}

	override remove() {
		super.remove();
		// Remove this label's parent too, unless it has already been removed
		if (this.drawableState.hasDrawable(this._parentId)) {
			this.parent?.remove();
		}
		// Mutate the linked collection fields to remove their references to this label
		for (const link of this.links) {
			link.Mutator.unlink();
		}
		this._links = [];
	}

	override get isInsightMeasurement() {
		return this.parent.isInsightMeasurement;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return true;
	}

	/**
	 * Shortcut to check if the insight detection class is a base offset marker.
	 */
	get isInsightBaseOffsetDetectionClass() {
		if (isMarker(this.parent)) {
			return this.parent.isInsightBaseOffsetDetectionClass;
		}
		return false;
	}

	approveInsightFlag(): void {
		if (!this.parent.isApprovableInsightDrawable()) {
			return;
		}
		this.parent.approveInsightFlag();
	}

	override get isVisible() {
		return this.parent.isVisible;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableLabel {
		return new CollectionModelDrawableLabel(
			newParentDrawableState,
			this.id,
			this.x,
			this.y,
			this.parentId,
			this._text,
			this._links
		);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return (
			other instanceof CollectionModelDrawableLabel &&
			this.x === other.x &&
			this.y === other.y &&
			this._text.join('') === other._text.join('') &&
			this._links.join('') === other._links.join('') &&
			this._parentId === other._parentId &&
			this._equals(other)
		);
	}
}
